export const Suppliers = (json: ISupplier): ISupplierModel => {
	const { contact_person, postal_code, tax_id, bank_account, payment_terms, is_active, active_price_list, ...rest } =
		json

	return {
		...rest,
		contactPerson: contact_person,
		postalCode: postal_code,
		taxId: tax_id,
		bankAccount: bank_account,
		paymentTerms: payment_terms,
		isActive: is_active,
		activePriceList: active_price_list
			? {
					createdAt: active_price_list.created_at,
					validFrom: active_price_list.valid_from,
					validUntil: active_price_list.valid_until,
				}
			: null,
	}
}

export const supplierModelToJSON = (obj: ISupplierModel): ISupplier => {
	const { contactPerson, postalCode, taxId, bankAccount, paymentTerms, isActive, activePriceList, ...rest } = obj

	return {
		...rest,
		contact_person: contactPerson,
		postal_code: postalCode,
		tax_id: taxId,
		bank_account: bankAccount,
		payment_terms: paymentTerms,
		is_active: isActive,
		active_price_list: activePriceList
			? {
					created_at: activePriceList.createdAt,
					valid_from: activePriceList.validFrom,
					valid_until: activePriceList.validUntil,
				}
			: null,
	}
}

export const PriceListProduct = (json: IPriceListProduct): IPriceListProductModel => {
	const {
		raw_materials,
		supplier_material_id,
		supplier_material_name,
		logistic_minimum_amount,
		logistic_minimum_unit,
		raw_material_name,
		unit_price,
		price_per_unit,
		source,
		...rest
	} = json

	return {
		...rest,
		source,
		isManual: source === 'manual',
		rawMaterials: raw_materials,
		supplierMaterialId: supplier_material_id,
		supplierMaterialName: supplier_material_name,
		rawMaterialName: raw_material_name,
		logisticMinimumAmount: logistic_minimum_amount,
		logisticMinimumUnit: logistic_minimum_unit,
		unitPrice: unit_price,
		pricePerUnit: price_per_unit,
	}
}

export const PriceList = (json: IPriceList): IPriceListModel => {
	const {
		is_active,
		is_fully_matched,
		product_count,
		supplier_id,
		uploaded_at,
		valid_until,
		unmatched_products_count,
		name,
		...rest
	} = json

	return {
		...rest,
		isActive: is_active,
		isFullyMatched: is_fully_matched,
		productCount: product_count,
		unmatchedProductsCount: unmatched_products_count,
		supplierId: supplier_id,
		uploadedAt: uploaded_at,
		validUntil: valid_until,
		name: name ?? '',
	}
}

export const MatchProduct = (json: IMatchProduct): IMatchProductModel => ({
	...json,
	rawMaterial: null,
})

export const RawMaterial = (json: IRawMaterial): IMatchProduct => {
	const { id, name } = json
	return { id, name }
}

export const MatchedProductToJson = (obj: IMatchProductModel): IRawMaterialSend => ({
	supplier_product: obj.id,
	raw_material: obj.rawMaterial?.id ?? 0,
})

const DeliveryHours = (json: IDeliveryHours): IDeliveryHoursModel => {
	const { delivery_from, delivery_to, hour_from, hour_to } = json
	return {
		deliveryFrom: delivery_from,
		deliveryTo: delivery_to,
		hourFrom: hour_from,
		hourTo: hour_to,
	}
}

export const PurchaseConditions = (json: IPurchaseConditions): IPurchaseConditionsModel => {
	const deliveryHours = json?.delivery_hours?.length ? json.delivery_hours.map(dh => DeliveryHours(dh)) : []
	return {
		paymentDeadline: json?.payment_deadline ? String(json.payment_deadline) : '',
		deliveryTime: json?.delivery_time ? String(json.delivery_time) : '',
		logisticMin: json?.logistic_min ? String(Number(json.logistic_min) / 100) : '',
		minExpTime: json?.min_exp_time ? String(json.min_exp_time) : '',
		deliveryHours,
	}
}

const DeliveryHoursToJson = (data: IDeliveryHoursModel): IDeliveryHours => {
	const { deliveryFrom, deliveryTo, hourFrom, hourTo } = data
	return {
		delivery_from: deliveryFrom,
		delivery_to: deliveryTo,
		hour_from: hourFrom,
		hour_to: hourTo,
	}
}

export const PurchaseConditionsToJson = (json: IPurchaseConditionsModel): IPurchaseConditions => {
	const { paymentDeadline, deliveryTime, logisticMin, minExpTime, deliveryHours, ...rest } = json
	const delivery_hours = deliveryHours?.length ? deliveryHours.map(dh => DeliveryHoursToJson(dh)) : []
	return {
		...rest,
		payment_deadline: Number(paymentDeadline),
		delivery_time: Number(deliveryTime),
		logistic_min: Math.trunc(Number(logisticMin) * 100),
		min_exp_time: Number(minExpTime),
		delivery_hours,
	}
}

const PriceListImportWarningProduct = (json: IPriceListImportWarningProduct): IPriceListImportWarningProductModel => ({
	supplierMaterialId: json.supplier_material_id,
	supplierMaterialName: json.supplier_material_name,
	crmId: json.crm_id,
})

const PriceListImportWarning = (json: IPriceListImportWarning): IPriceListImportWarningModel => ({
	message: json.message,
	count: json.count,
	products: json.products?.map(product => PriceListImportWarningProduct(product)) ?? [],
})

export const PriceListImportResult = (json: IPriceListImportResult): IPriceListImportResultModel => ({
	imported: json.imported,
	errors: json.errors ?? [],
	importedIds: json.importedIds ?? json.imported_ids ?? [],
	unmatched:
		json.unmatched?.map(item => ({
			supplierProductId: item.supplierProductId ?? item.supplier_product_id ?? 0,
			name: item.name,
		})) ?? [],
	warnings: json.warnings?.map(warning => PriceListImportWarning(warning)) ?? [],
})

export const EditSupplierPriceListResult = (
	json: IEditSupplierPriceListResponse,
): IEditSupplierPriceListResultModel => {
	const importResult = json.importResult ?? json.import_result

	return {
		message: json.message,
		updatedFields: json.updatedFields ?? json.updated_fields ?? null,
		importResult: importResult ? PriceListImportResult(importResult) : null,
	}
}
